import { useState, useEffect } from 'react';
import { getUser, getApiBaseUrl } from '../utils/api';

const OverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalDispatched: 0,
    totalAdvanceReceived: 0,
    totalBalancePending: 0,
    lowStockCount: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch dashboard stats
        const statsResponse = await fetch(`${getApiBaseUrl()}/api/orders/dashboard/stats`, {
          credentials: 'include'
        });

        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }

        // Fetch user info
        await getUser();
      } catch (error) {
        console.error('Error fetching overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-300 border-t-blue-600"></div>
          <span className="text-gray-600 font-medium">Loading overview...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back! 👋</h1>
            <p className="text-gray-600">Here's your business overview for today</p>
          </div>
          <div className="text-right">
            <img
              src="/logo.png"
              alt="Ekdunt Arts"
              className="w-16 h-16 mx-auto mb-2"
            />
            <p className="text-sm text-gray-500">Ganesh Idol Booking System</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8">
        {/* Total Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
            <div className="text-3xl">📋</div>
          </div>
        </div>

        {/* Total Dispatched */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Orders Dispatched</p>
              <p className="text-2xl font-bold text-blue-600">{stats.totalDispatched}</p>
            </div>
            <div className="text-3xl">🚚</div>
          </div>
        </div>

        {/* Advance Received */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Advance Received</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalAdvanceReceived)}</p>
            </div>
            <div className="text-3xl">💰</div>
          </div>
        </div>

        {/* Balance Pending */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Balance Pending</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalBalancePending)}</p>
            </div>
            <div className="text-3xl">⏳</div>
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className={`rounded-lg shadow-sm border p-6 ${stats.lowStockCount > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium mb-1 ${stats.lowStockCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                Low Stock Items
              </p>
              <p className={`text-2xl font-bold ${stats.lowStockCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {stats.lowStockCount}
              </p>
            </div>
            <div className="text-3xl">{stats.lowStockCount > 0 ? '⚠️' : '✅'}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="text-3xl mb-2">🛒</div>
            <p className="font-medium text-gray-900">New Retail Order</p>
            <p className="text-sm text-gray-600">Create customer order</p>
          </div>
          <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="text-3xl mb-2">📦</div>
            <p className="font-medium text-gray-900">New Wholesale Order</p>
            <p className="text-sm text-gray-600">Bulk business order</p>
          </div>
          <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="text-3xl mb-2">📋</div>
            <p className="font-medium text-gray-900">Manage Inventory</p>
            <p className="text-sm text-gray-600">Update stock & products</p>
          </div>
          <div className="text-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <div className="text-3xl mb-2">💰</div>
            <p className="font-medium text-gray-900">View Finances</p>
            <p className="text-sm text-gray-600">Settlement reports</p>
          </div>
        </div>
      </div>

      {/* Recent Activity Placeholder */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-4">📊</div>
          <p>Activity feed coming soon...</p>
          <p className="text-sm mt-2">Track recent orders, payments, and inventory changes</p>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
